import type { GameModel, ReadOnlyGameModel } from 'yage/game/GameModel';
import { Transform } from 'yage/schemas/entity/Transform';
import { AirportStaff, AirportAgent } from '../components/index.js';
import { CONFIG, DISPATCH, internationalTraveller, type TravellerRoute } from '../domain/config.js';
import { PHASE } from '../domain/operations.js';
import { planes } from './operations.js';
import { tier } from '../domain/airports.js';
import { shiftsActive, skillSpeed, RALLY_RADIUS, RALLY_SPEED, SERVICE_POLICIES } from '../domain/act2.js';
import { staffTrait } from '../domain/staff.js';
import { career, ids, state, stations, distance } from './world.js';

/** Alpha wage table per shift, by role, before the airport's cost multiplier (halved). */
export const WAGES: Record<string, number> = { clerk: 120, crew: 120, porter: 90, cleaner: 80, runner: 100, pilot: 150 };
/** Upkeep per open department per shift. */
export const UPKEEP = 25;
/** The Board advance covers at most this many shifts of dues in total. */
export const ADVANCE_CAP_SHIFTS = 3;

/** Wages and upkeep due at the end of this shift. */
export function shiftDues(g: GameModel): number {
    const e = state(g), mult = tier(e).costMult / 2;
    const wages = ids(g, AirportStaff).reduce((n, id) => { const s = g.getTypedUnsafe(AirportStaff, id); return n + (WAGES[s.role] ?? 0) * (staffTrait(s.recordId) === 'Steady' ? 0.9 : 1) * (1 + 0.25 * (s.skill - 1)); }, 0);
    const upkeep = stations(g).filter(s => s.unlocked).length * UPKEEP;
    return Math.round(wages * mult * SERVICE_POLICIES[e.policyService]?.wages + upkeep);
}

/**
 * ECON-005 shift settlement. Dues come from the wallet first; any shortfall is covered by an interest-free Board
 * advance up to ADVANCE_CAP_SHIFTS × dues, so the wallet never goes negative. Whatever even the advance cannot cover
 * is unpaid (two unpaid shifts slow staff by 20%). A fully paid shift repays the advance from its profit.
 */
export function settleShift(g: GameModel): { paid: number; advanced: number; repaid: number; unpaid: boolean } {
    const e = state(g), w = career(g);
    if (!shiftsActive(e)) return { paid: 0, advanced: 0, repaid: 0, unpaid: false };
    const due = shiftDues(g), fromWallet = Math.min(w.money, due), short = due - fromWallet;
    w.money -= fromWallet;
    const advanced = Math.max(0, Math.min(short, ADVANCE_CAP_SHIFTS * due - w.advance));
    w.advance += advanced;
    let repaid = 0;
    if (!short && w.advance > 0) {
        repaid = Math.min(w.advance, Math.max(0, e.shiftEarned - due), w.money);
        w.money -= repaid; w.advance -= repaid;
    }
    return { paid: fromWallet + advanced, advanced, repaid, unpaid: short - advanced > 0 };
}

/** A captain within RALLY_RADIUS of a department rallies its staff (one bonus, however many captains). */
export function rallied(g: GameModel | ReadOnlyGameModel, stage: number): boolean {
    const desk = CONFIG.stages[stage];
    return !!desk && ids(g, AirportAgent).some(a => distance(g.getTypedUnsafe(Transform, a), desk) <= RALLY_RADIUS);
}
/** Service speed factor from the staff at a desk: best skill, Quick Hands and a captain's rally. */
export function deskStaffFactor(g: GameModel | ReadOnlyGameModel, deskStaff: number[], stage: number): number {
    const staff = deskStaff.map(id => g.getTypedUnsafe(AirportStaff, id));
    const skill = Math.max(...staff.map(s => skillSpeed(s.skill)));
    const quick = staff.some(s => staffTrait(s.recordId) === 'Quick Hands') ? 1.1 : 1;
    const e = state(g), act2 = shiftsActive(e);
    return skill * quick * (act2 && rallied(g, stage) ? RALLY_SPEED : 1) * (act2 ? SERVICE_POLICIES[e.policyService]?.speed ?? 1 : 1);
}
/** Training cost for the next pip (MGT-003). */
export function trainingCost(skill: number, costMult: number): number { return Math.ceil(400 * costMult * skill); }

/** A traveller needs this much time (3 airport minutes) before the next departure to stop at a shop (MGT-010). */
export const RETAIL_DWELL = 900;
/** Per-traveller routing options: international (serial), shop dwell (next departure), premium (VIP); Act I keeps the legacy shop rule. */
export function travellerRoute(g: GameModel | ReadOnlyGameModel, serial: number, vip: boolean): TravellerRoute {
    const e = state(g), ss = stations(g), international = internationalTraveller(serial, ss);
    if (!shiftsActive(e)) return { international, legacy: true };
    const soonest = Math.min(...planes(g).filter(p => p.phase === PHASE.BOARDING).map(p => p.scheduledDepartureFrame - g.frame));
    return { international, retail: !(soonest < RETAIL_DWELL), premium: vip };
}

/** Flight Dispatch is staffed when someone works its desk (MGT-011); Act II only. */
export function dispatchStaffed(g: GameModel | ReadOnlyGameModel): boolean {
    const s = stations(g)[DISPATCH];
    return shiftsActive(state(g)) && !!s?.unlocked && s.activeWorkers > 0;
}
/** The next 30 airport minutes of departures and arrivals, only with a staffed Flight Dispatch. */
export const FORECAST_FRAMES = 9000;
export function dispatchForecast(g: GameModel | ReadOnlyGameModel): { gate: number; flight: number; departs: number; arrives: number | null }[] {
    if (!dispatchStaffed(g)) return [];
    return planes(g).map(p => ({ gate: p.gate, flight: p.flightNumber, departs: p.scheduledDepartureFrame - g.frame, arrives: p.expectedArrivalFrame >= 0 ? p.expectedArrivalFrame - g.frame : null }))
        .filter(f => f.departs >= 0 && f.departs <= FORECAST_FRAMES || f.arrives !== null && f.arrives >= 0 && f.arrives <= FORECAST_FRAMES)
        .sort((a, b) => Math.min(a.departs, a.arrives ?? Infinity) - Math.min(b.departs, b.arrives ?? Infinity));
}
